import { observer } from "mobx-react-lite";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDebounce } from "use-debounce";

import { PATHS } from "@/lib/router";
import { useTran } from "@/hooks";
import { useCompanyStore } from "../hooks";
import { COMPANY_STEPS } from "../utils/company.steps";
import CompanySetupHeader from "./CompanySetupHeader";
import CompanySetupStepper from "./CompanySetupStepper";
import CompanySetupStepContent from "./CompanySetupStepContent";
import CompanySetupFooter from "./CompanySetupFooter";

const getCompanyId = (data) => data?.id ?? data?._id ?? data?.companyId ?? null;

const buildCompanyFormPatch = (company) => {
  if (!company || typeof company !== "object") return null;
  const patch = {};
  const name = company.companyName ?? company.name;
  if (name) patch.companyName = name;
  if (company.country) patch.country = company.country;
  if (company.industry) patch.industry = company.industry;
  if (company.employeesRange) patch.employeesRange = company.employeesRange;
  if (company.revenueRange) patch.revenueRange = company.revenueRange;
  if (Array.isArray(company.serviceCountries))
    patch.serviceCountries = company.serviceCountries;
  if (Array.isArray(company.objectives)) patch.objectives = company.objectives;
  return Object.keys(patch).length > 0 ? patch : null;
};

const CompanySetupWizard = observer(
  ({
    isDialog = false,
    companyId = null,
    companyData = null,
    onCompleted,
    compact = false,
  }) => {
    const { t } = useTran();
    const navigate = useNavigate();
    const location = useLocation();
    const isFromRegister = location.state?.from === "register";

    const {
      step,
      form,
      companies,
      setStep,
      validateStep,
      canContinue,
      createCompany,
      fetchPublicCompanies,
      hydrateForm,
      reset,
      clearDraft,
      setCompany,
      useSampleData,
    } = useCompanyStore();

    const hydratedCompanyRef = useRef(null);
    const hasFetchedCompaniesRef = useRef(false);
    const [touched, setTouched] = useState({});
    const [attempted, setAttempted] = useState(false);
    const [errors, setErrors] = useState({});
    const [companyNameStatus, setCompanyNameStatus] = useState("idle");
    const [debouncedCompanyName] = useDebounce(form.companyName, 400);

    const resolvedCompanyId = useMemo(
      () => companyId ?? getCompanyId(companyData),
      [companyId, companyData],
    );

    useEffect(() => {
      const resolvedCompanyIdFromData = getCompanyId(companyData);

      if (!companyData || !resolvedCompanyIdFromData) {
        hydratedCompanyRef.current = null;
        if (isDialog) {
          reset();
        }
        return;
      }

      if (hydratedCompanyRef.current === resolvedCompanyIdFromData) {
        return;
      }

      hydratedCompanyRef.current = resolvedCompanyIdFromData;
      const patch = buildCompanyFormPatch(companyData);
      if (patch) {
        hydrateForm(patch);
      }
    }, [companyData, hydrateForm, isDialog, reset]);

    useEffect(() => {
      if (isDialog || !isFromRegister) return;
      if (companyData || resolvedCompanyId) return;
      clearDraft();
      reset({ persist: false });
      setCompany(null);
      setTouched({});
      setErrors({});
      setAttempted(false);
    }, [
      isDialog,
      isFromRegister,
      companyData,
      resolvedCompanyId,
      clearDraft,
      reset,
      setCompany,
    ]);

    useEffect(() => {
      if (step !== 1) return;
      const rawName = debouncedCompanyName?.trim() || "";
      if (!rawName) {
        setCompanyNameStatus("idle");
        return;
      }

      let isActive = true;

      const checkAvailability = async () => {
        setCompanyNameStatus("checking");
        try {
          if (!hasFetchedCompaniesRef.current && companies.length === 0) {
            hasFetchedCompaniesRef.current = true;
            try {
              await fetchPublicCompanies();
            } catch (error) {
              hasFetchedCompaniesRef.current = false;
              throw error;
            }
          }

          if (!isActive) return;

          const normalizedName = rawName.toLowerCase();
          const match = companies.find((company) => {
            const name = (company?.companyName || company?.name || "")
              .trim()
              .toLowerCase();
            return name === normalizedName;
          });
          const matchId = match?._id ?? match?.id ?? match?.companyId ?? null;
          const isSameCompany =
            match &&
            resolvedCompanyId &&
            String(matchId) === String(resolvedCompanyId);

          if (match && !isSameCompany) {
            setCompanyNameStatus("taken");
            return;
          }

          setCompanyNameStatus("available");
        } catch (error) {
          if (!isActive) return;
          setCompanyNameStatus("error");
        }
      };

      checkAvailability();

      return () => {
        isActive = false;
      };
    }, [
      companies,
      debouncedCompanyName,
      fetchPublicCompanies,
      resolvedCompanyId,
      step,
    ]);

    useEffect(() => {
      setAttempted(false);
    }, [step]);

    const accessByStep = useMemo(() => {
      const completed = {};
      for (let i = 1; i <= COMPANY_STEPS.length; i += 1) {
        if (i === 5) {
          completed[i] =
            completed[4] ?? Object.keys(validateStep(4, form)).length === 0;
        } else {
          completed[i] = Object.keys(validateStep(i, form)).length === 0;
        }
      }

      const access = {};
      for (let i = 1; i <= COMPANY_STEPS.length; i += 1) {
        let allowed = true;
        for (let s = 1; s < i; s += 1) {
          if (!completed[s]) {
            allowed = false;
            break;
          }
        }
        access[i] = allowed;
      }

      return access;
    }, [form, validateStep]);

    const canNavigateToStep = (targetStep) => {
      if (targetStep <= step) return true;
      return accessByStep[targetStep] ?? false;
    };

    const goToStep = (targetStep) => {
      if (canNavigateToStep(targetStep)) {
        setStep(targetStep);
      }
    };

    const getFieldError = (key) =>
      attempted || touched[key] ? errors[key] : undefined;
    const handleBlur = (key) => () =>
      setTouched((prev) => ({ ...prev, [key]: true }));

    const companyNameAvailabilityError =
      companyNameStatus === "taken"
        ? t("Company name is already in use.")
        : undefined;
    const companyNameStatusMessage =
      companyNameStatus === "checking"
        ? t("Checking availability...")
        : companyNameStatus === "available"
          ? t("Company name is available.")
          : companyNameStatus === "error"
            ? t("Unable to verify company name right now.")
            : "";
    const companyNameDescription =
      companyNameStatus === "taken" ? undefined : companyNameStatusMessage;
    const companyNameDescriptionClassName =
      companyNameStatus === "available"
        ? "text-emerald-600 text-xs"
        : companyNameStatus === "error"
          ? "text-amber-600 text-xs"
          : "text-xs text-muted-foreground";
    const companyNameError =
      companyNameAvailabilityError || getFieldError("companyName");
    const isCompanyNamePending = companyNameStatus === "checking";
    const isCompanyNameTaken = companyNameStatus === "taken";
    const canContinueStep =
      step === 1
        ? canContinue && !isCompanyNamePending && !isCompanyNameTaken
        : canContinue;

    const nextStep = () => {
      setAttempted(true);
      const nextErrors = validateStep(step, form);
      if (step === 1 && isCompanyNameTaken) {
        nextErrors.companyName = t("Company name is already in use.");
      }
      setErrors(nextErrors);
      if (Object.keys(nextErrors).length > 0) return;
      if (step === 1 && isCompanyNamePending) return;
      setStep(Math.min(4, step + 1));
    };

    const prevStep = () => setStep(Math.max(1, step - 1));

    const handleSubmit = async (nextPath) => {
      try {
        await createCompany(resolvedCompanyId);
        if (isDialog) {
          onCompleted?.();
        } else if (nextPath) {
          navigate(nextPath);
        }
      } catch (error) {
        // toast handled in store
      }
    };

    const handleStart = () => handleSubmit(PATHS.DASHBOARD);
    const handleSaveAndExit = () => handleSubmit(PATHS.DASHBOARD);
    const handleReview = () => setStep(5);

    const handleSampleData = () => {
      useSampleData();
      setTouched({
        companyName: true,
        country: true,
        industry: true,
        employeesRange: true,
        revenueRange: true,
      });
    };

    const handleReset = () => {
      reset();
      setTouched({});
      setErrors({});
      setAttempted(false);
    };

    return (
      <div className="space-y-6 w-full">
        <CompanySetupHeader
          isDialog={isDialog}
          onSampleData={handleSampleData}
          onReset={handleReset}
        />
        <div className="grid gap-6 lg:grid-cols-[240px_1fr] p-4 bg-card rounded-radius border border-border">
          <CompanySetupStepper
            canNavigateToStep={canNavigateToStep}
            goToStep={goToStep}
          />
          <div className="flex flex-col justify-between">
            <CompanySetupStepContent
              errors={errors}
              getFieldError={getFieldError}
              handleBlur={handleBlur}
              companyNameError={companyNameError}
              companyNameDescription={companyNameDescription}
              companyNameDescriptionClassName={companyNameDescriptionClassName}
            />
            <CompanySetupFooter
              compact={compact}
              isDialog={isDialog}
              canContinueStep={canContinueStep}
              nextStep={nextStep}
              prevStep={prevStep}
              handleSaveAndExit={handleSaveAndExit}
              handleReview={handleReview}
              handleStart={handleStart}
            />
          </div>
        </div>
      </div>
    );
  },
);

export default CompanySetupWizard;
